import React from "react";
import { Link } from "react-router-dom";
import { cn } from "../../DashboardPage/lib/utils";

/**
 * The single definition of a pitch status badge.
 *
 * Extracted verbatim from DashboardTableRow so every surface that shows a
 * pitch status renders identical markup. "In flight" states use the primary
 * scale; terminal success and error keep conventional semantic hues,
 * desaturated. Admins see abbreviated labels for the compound states.
 */

const baseClasses =
  "inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium border";

const pending = "bg-primary-50 text-primary-800 border-neutral-200";
const success = "bg-success-50 text-success-800 border-success-200";
const neutral = "bg-neutral-100 text-neutral-700 border-neutral-200";
const warning = "bg-warning-50 text-warning-800 border-warning-200";
const danger = "bg-danger-50 text-danger-700 border-danger-200";

/**
 * The badge shell and tone tokens, exported so surfaces with a *different*
 * status vocabulary (e.g. email-log delivery states, which are unrelated to
 * pitch statuses) can render the same badge without this file having to know
 * about them. Reuse these rather than re-typing the colour classes.
 */
export const badgeBaseClass = baseClasses;
export const badgeTones = { pending, success, neutral, warning, danger };

export function getStatusPresentation(status: string, isAdmin = false) {
  switch (status) {
    case "submitted":
      return { tone: pending, label: "Submitted" };
    case "delivered":
      return { tone: success, label: "Delivered" };
    case "draft":
      return { tone: neutral, label: "Draft" };
    case "submitted - update pending":
      return {
        tone: isAdmin ? warning : pending,
        label: isAdmin ? "Submitted Upd. Pend" : "Submitted",
      };
    case "delivered - update pending":
      return {
        tone: isAdmin ? warning : success,
        label: isAdmin ? "Delivered Upd. Pend" : "Delivered",
      };
    case "delivered - re-delivery needed":
      return {
        tone: isAdmin ? danger : success,
        label: isAdmin ? "Re-delivery" : "Delivered",
      };
    default:
      return { tone: neutral, label: status };
  }
}

interface StatusBadgeProps {
  status: string;
  /** Admins see abbreviated labels and distinct tones for compound states. */
  isAdmin?: boolean;
  /** When set the badge renders as a link (the dashboard links draft rows to the editor). */
  href?: string;
  className?: string;
}

export default function StatusBadge({
  status,
  isAdmin = false,
  href,
  className,
}: StatusBadgeProps) {
  const { tone, label } = getStatusPresentation(status, isAdmin);

  if (href) {
    return (
      <Link
        to={href}
        className={cn(
          baseClasses,
          tone,
          "cursor-pointer hover:bg-neutral-200 transition-colors",
          className
        )}
      >
        {label}
      </Link>
    );
  }

  return <span className={cn(baseClasses, tone, className)}>{label}</span>;
}
